import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { 
    BarChart3,
    Calendar,
    TrendingUp,
    AlertTriangle,
    CheckCircle,
    XCircle,
    Award,
    Fuel,
    ClipboardCheck,
    Activity,
    Download,
    RefreshCw,
    Ship,
    Trophy,
    FileText
} from 'lucide-react';
import { toast } from 'sonner';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    LineChart,
    Line,
    PieChart,
    Pie,
    Cell
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ControleOperacionalPage = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [historico, setHistorico] = useState(null);
    const [exportandoPdf, setExportandoPdf] = useState(false);
    
    // Filtro de período - padrão: último mês
    const hoje = new Date();
    const primeiroDiaMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    
    const [dataInicio, setDataInicio] = useState(primeiroDiaMes.toISOString().split('T')[0]);
    const [dataFim, setDataFim] = useState(hoje.toISOString().split('T')[0]);
    const [totalDias, setTotalDias] = useState(0);
    const [embarcacoesSelecionadas, setEmbarcacoesSelecionadas] = useState([]);
    const [embarcacoesDisponiveis, setEmbarcacoesDisponiveis] = useState([]);
    const embarcacaoIdsParam = embarcacoesSelecionadas.join(',');

    // Calcular total de dias quando as datas mudam
    useEffect(() => {
        if (dataInicio && dataFim) {
            const inicio = new Date(dataInicio);
            const fim = new Date(dataFim);
            const diff = Math.ceil((fim - inicio) / (1000 * 60 * 60 * 24)) + 1;
            setTotalDias(diff > 0 ? diff : 0);
        }
    }, [dataInicio, dataFim]);

    const fetchData = useCallback(async () => {
        if (!dataInicio || !dataFim) {
            toast.error('Selecione o período');
            return;
        }
        
        setLoading(true);
        const historicoRequest = axios.get(`${API}/controle-operacional/historico?meses=12${embarcacaoIdsParam ? `&embarcacao_ids=${encodeURIComponent(embarcacaoIdsParam)}` : ''}`);
        try {
            const controleRes = await axios.get(`${API}/controle-operacional?data_inicio=${dataInicio}&data_fim=${dataFim}${embarcacaoIdsParam ? `&embarcacao_ids=${encodeURIComponent(embarcacaoIdsParam)}` : ''}`);
            setData(controleRes.data);
            if (controleRes.data.embarcacoes_disponiveis) {
                setEmbarcacoesDisponiveis(controleRes.data.embarcacoes_disponiveis);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao carregar dados');
        } finally {
            setLoading(false);
        }
        try {
            const historicoRes = await historicoRequest;
            setHistorico(historicoRes.data);
        } catch (error) {
            console.error('Erro ao carregar histórico operacional:', error);
        }
    }, [dataInicio, dataFim, embarcacaoIdsParam]);

    useEffect(() => {
        fetchData();
    }, []);

    const handleExportCSV = () => {
        if (!data) return;
        
        const linhas = [
            'CONTROLE OPERACIONAL - RELATÓRIO',
            `Período: ${dataInicio} a ${dataFim}`,
            `Total de Dias: ${totalDias}`,
            '',
            '=== EXECUÇÃO DA OPERAÇÃO DE BUNKER (40%) ===',
            `Total de Fornecimento (m³): ${data.execucao_bunker.total_fornecimento}`,
            `Média de Operações/Dia: ${data.execucao_bunker.media_operacoes_dia}`,
            `Tempo Médio de Operação (dias): ${data.execucao_bunker.tempo_medio_operacao_dias}`,
            `Total de Operações: ${data.execucao_bunker.total_operacoes}`,
            `Avaliação Média (%): ${data.execucao_bunker.avaliacao_media_percent}`,
            `NOTA: ${data.execucao_bunker.nota}/${data.execucao_bunker.peso_maximo}`,
            '',
            '=== DESEMPENHO NAS VISTORIAS (30%) ===',
            `Total de Não Conformidades: ${data.desempenho_vistorias.total_nao_conformidades}`,
            `NOTA: ${data.desempenho_vistorias.nota}/${data.desempenho_vistorias.peso_maximo}`,
            '',
            '=== PERFORMANCE GERAL NO CICLO (30%) ===',
            `Ciclos Analisados: ${data.performance_ciclo.ciclos_analisados}`,
            `Nota VLSFO: ${data.performance_ciclo.nota_vlsfo}/15`,
            `Nota MGO: ${data.performance_ciclo.nota_mgo}/15`,
            `NOTA: ${data.performance_ciclo.nota}/${data.performance_ciclo.peso_maximo}`,
            '',
            '=== RESULTADO FINAL ===',
            `Nota Execução Bunker: ${data.resultado.nota_execucao_bunker}`,
            `Nota Vistorias: ${data.resultado.nota_vistorias}`,
            `Nota Performance Ciclo: ${data.resultado.nota_performance_ciclo}`,
            `NOTA TOTAL: ${data.resultado.nota_total}/${data.resultado.nota_maxima}`,
            `CLASSIFICAÇÃO: ${data.resultado.classificacao}`
        ];
        
        const blob = new Blob([linhas.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `controle_operacional_${dataInicio}_${dataFim}.csv`;
        link.click();
        toast.success('Relatório exportado');
    };

    const handleExportPDF = async () => {
        if (!data) return;
        setExportandoPdf(true);
        try {
            const params = new URLSearchParams({ data_inicio: dataInicio, data_fim: dataFim });
            if (embarcacaoIdsParam) params.set('embarcacao_ids', embarcacaoIdsParam);
            const response = await axios.get(`${API}/controle-operacional/relatorio-pdf?${params.toString()}`, { responseType: 'blob' });
            const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.download = `controle_operacional_${dataInicio}_${dataFim}.pdf`;
            link.click();
            URL.revokeObjectURL(url);
            toast.success('PDF gerado com sucesso');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao gerar PDF');
        } finally {
            setExportandoPdf(false);
        }
    };

    const getClassificacaoColor = (classificacao) => {
        switch (classificacao) {
            case 'Excelente': return 'text-green-400 bg-green-900/30';
            case 'Bom': return 'text-blue-400 bg-blue-900/30';
            case 'Regular': return 'text-yellow-400 bg-yellow-900/30';
            default: return 'text-red-400 bg-red-900/30';
        }
    };

    const getNotaColor = (nota, max) => {
        const percent = (nota / max) * 100;
        if (percent >= 80) return 'text-green-400';
        if (percent >= 50) return 'text-yellow-400';
        return 'text-red-400';
    };

    const toggleEmbarcacao = (id) => {
        setEmbarcacoesSelecionadas(atual => atual.includes(id) ? atual.filter(item => item !== id) : [...atual, id]);
    };

    const COLORS = ['#22c55e', '#3b82f6', '#eab308', '#ef4444'];

    // Dados para gráficos
    const pieData = data ? [
        { name: 'Execução Bunker', value: data.resultado.nota_execucao_bunker, max: 40 },
        { name: 'Vistorias', value: data.resultado.nota_vistorias, max: 30 },
        { name: 'Performance Ciclo', value: data.resultado.nota_performance_ciclo, max: 30 }
    ] : [];

    const gradeData = data ? Object.entries(data.performance_ciclo.por_grade).map(([grade, info]) => ({
        grade,
        percentual: info.percentual_medio,
        limite: info.limite,
        conforme: info.conforme
    })) : [];

    return (
        <div className="space-y-6" data-testid="controle-operacional-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white">Controle Operacional</h1>
                    <p className="text-slate-400 text-sm">Monitoramento e avaliação consolidada das operações</p>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleExportCSV}
                        disabled={!data}
                        data-testid="export-csv-btn"
                    >
                        <Download className="w-4 h-4 mr-2" />
                        Exportar CSV
                    </Button>
                    <Button
                        size="sm"
                        onClick={handleExportPDF}
                        disabled={!data || exportandoPdf}
                        data-testid="export-pdf-btn"
                        className="bg-cyan-600 hover:bg-cyan-700"
                    >
                        <FileText className="w-4 h-4 mr-2" />
                        {exportandoPdf ? 'Gerando PDF...' : 'Baixar PDF'}
                    </Button>
                </div>
            </div>

            {/* Filtro de Período */}
            <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Filtro de Período (Obrigatório)
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-wrap items-end gap-4">
                        <div>
                            <Label className="text-xs">Data Inicial</Label>
                            <Input
                                type="date"
                                value={dataInicio}
                                onChange={(e) => setDataInicio(e.target.value)}
                                className="w-40"
                                data-testid="data-inicio"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Data Final</Label>
                            <Input
                                type="date"
                                value={dataFim}
                                onChange={(e) => setDataFim(e.target.value)}
                                className="w-40"
                                data-testid="data-fim"
                            />
                        </div>
                        <div className="px-4 py-2 bg-slate-800 rounded-lg">
                            <div className="text-xs text-slate-400">Total de Dias</div>
                            <div className="text-xl font-bold text-cyan-400">{totalDias}</div>
                        </div>
                        <div>
                            <Label className="text-xs">Embarcações</Label>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button variant="outline" data-testid="filtro-embarcacoes-multiplas" className="w-52 h-9 justify-between bg-slate-800 border-slate-700 text-white hover:bg-slate-700">
                                        {embarcacoesSelecionadas.length ? `${embarcacoesSelecionadas.length} selecionada(s)` : 'Todas as Embarcações'}
                                        <Ship className="w-4 h-4 text-cyan-400" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-72 max-h-80 overflow-y-auto bg-slate-900 border-slate-700 p-2" align="start">
                                    <Button variant="ghost" size="sm" data-testid="limpar-filtro-embarcacoes" className="w-full justify-start text-slate-300 hover:text-white" onClick={() => setEmbarcacoesSelecionadas([])}>
                                        Todas as Embarcações
                                    </Button>
                                    {embarcacoesDisponiveis.map(e => (
                                        <label key={e.id} htmlFor={`embarcacao-${e.id}`} className="flex items-center gap-2 px-2 py-2 text-sm text-slate-200 hover:bg-slate-800 cursor-pointer rounded">
                                            <Checkbox id={`embarcacao-${e.id}`} data-testid={`checkbox-embarcacao-${e.id}`} checked={embarcacoesSelecionadas.includes(e.id)} onCheckedChange={() => toggleEmbarcacao(e.id)} />
                                            <span>{e.nome}</span>
                                        </label>
                                    ))}
                                </PopoverContent>
                            </Popover>
                        </div>
                        <Button onClick={fetchData} disabled={loading} data-testid="buscar-btn">
                            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                            Buscar
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {loading && (
                <div className="text-center py-8 text-slate-400">
                    Carregando dados...
                </div>
            )}

            {data && (
                <>
                    {/* Resultado Final */}
                    <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-slate-700">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Award className="w-5 h-5 text-yellow-400" />
                                Resultado Final do Controle Operacional
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                                <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                                    <div className="text-sm text-slate-400">Execução Bunker</div>
                                    <div className={`text-3xl font-bold ${getNotaColor(data.resultado.nota_execucao_bunker, 40)}`}>
                                        {data.resultado.nota_execucao_bunker}
                                    </div>
                                    <div className="text-xs text-slate-500">/ 40%</div>
                                </div>
                                <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                                    <div className="text-sm text-slate-400">Vistorias</div>
                                    <div className={`text-3xl font-bold ${getNotaColor(data.resultado.nota_vistorias, 30)}`}>
                                        {data.resultado.nota_vistorias}
                                    </div>
                                    <div className="text-xs text-slate-500">/ 30%</div>
                                </div>
                                <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                                    <div className="text-sm text-slate-400">Performance Ciclo</div>
                                    <div className={`text-3xl font-bold ${getNotaColor(data.resultado.nota_performance_ciclo, 30)}`}>
                                        {data.resultado.nota_performance_ciclo}
                                    </div>
                                    <div className="text-xs text-slate-500">/ 30%</div>
                                </div>
                                <div className="text-center p-4 bg-cyan-900/30 border border-cyan-800 rounded-lg">
                                    <div className="text-sm text-cyan-400">Nota Total</div>
                                    <div className="text-4xl font-bold text-cyan-300">
                                        {data.resultado.nota_total}
                                    </div>
                                    <div className="text-xs text-cyan-500">/ 100%</div>
                                </div>
                                <div className={`text-center p-4 rounded-lg ${getClassificacaoColor(data.resultado.classificacao)}`}>
                                    <div className="text-sm opacity-80">Classificação</div>
                                    <div className="text-2xl font-bold">
                                        {data.resultado.classificacao}
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Grid de Indicadores */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        
                        {/* 1. Execução da Operação de Bunker (40%) */}
                        <Card className="bg-slate-900 border-slate-800">
                            <CardHeader>
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <Fuel className="w-4 h-4 text-orange-400" />
                                    Execução da Operação de Bunker
                                    <span className="ml-auto text-xs bg-orange-900/50 text-orange-400 px-2 py-0.5 rounded">
                                        Peso: 40%
                                    </span>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="p-3 bg-slate-800 rounded">
                                        <div className="text-xs text-slate-400">Total Fornecimento</div>
                                        <div className="text-lg font-bold text-white">
                                            {data.execucao_bunker.total_fornecimento.toLocaleString()} m³
                                        </div>
                                    </div>
                                    <div className="p-3 bg-slate-800 rounded">
                                        <div className="text-xs text-slate-400">Média Op./Dia</div>
                                        <div className="text-lg font-bold text-white">
                                            {data.execucao_bunker.media_operacoes_dia}
                                        </div>
                                    </div>
                                    <div className="p-3 bg-slate-800 rounded">
                                        <div className="text-xs text-slate-400">Tempo Médio Op.</div>
                                        <div className="text-lg font-bold text-white">
                                            {data.execucao_bunker.tempo_medio_operacao_dias} dias
                                        </div>
                                    </div>
                                    <div className="p-3 bg-slate-800 rounded">
                                        <div className="text-xs text-slate-400">Total Operações</div>
                                        <div className="text-lg font-bold text-white">
                                            {data.execucao_bunker.total_operacoes}
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="p-4 bg-gradient-to-r from-orange-900/30 to-slate-800 rounded-lg">
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <div className="text-xs text-slate-400">Avaliação Média</div>
                                            <div className="text-2xl font-bold text-orange-400">
                                                {data.execucao_bunker.avaliacao_media_percent}%
                                            </div>
                                            <div className="text-xs text-slate-500">
                                                {data.execucao_bunker.total_avaliacoes} avaliações
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-xs text-slate-400">Nota</div>
                                            <div className={`text-3xl font-bold ${getNotaColor(data.execucao_bunker.nota, 40)}`}>
                                                {data.execucao_bunker.nota}
                                            </div>
                                            <div className="text-xs text-slate-500">/ 40</div>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* 2. Desempenho nas Vistorias (30%) */}
                        <Card className="bg-slate-900 border-slate-800">
                            <CardHeader>
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <ClipboardCheck className="w-4 h-4 text-purple-400" />
                                    Desempenho nas Vistorias
                                    <span className="ml-auto text-xs bg-purple-900/50 text-purple-400 px-2 py-0.5 rounded">
                                        Peso: 30%
                                    </span>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="p-4 bg-gradient-to-r from-purple-900/30 to-slate-800 rounded-lg">
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <div className="text-xs text-slate-400">Total de Não Conformidades</div>
                                            <div className="text-3xl font-bold text-purple-400">
                                                {data.desempenho_vistorias.total_nao_conformidades}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-xs text-slate-400">Nota</div>
                                            <div className={`text-3xl font-bold ${getNotaColor(data.desempenho_vistorias.nota, 30)}`}>
                                                {data.desempenho_vistorias.nota}
                                            </div>
                                            <div className="text-xs text-slate-500">/ 30</div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="text-xs text-slate-400 space-y-1">
                                    <div className="flex justify-between">
                                        <span>0-1 NC</span>
                                        <span className="text-green-400">30%</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>2-4 NC</span>
                                        <span className="text-blue-400">20%</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>5-7 NC</span>
                                        <span className="text-yellow-400">10%</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>8+ NC</span>
                                        <span className="text-red-400">0%</span>
                                    </div>
                                </div>

                                {data.desempenho_vistorias.nao_conformidades.length > 0 && (
                                    <div className="max-h-40 overflow-y-auto">
                                        <Table>
                                            <TableHeader>
                                                <TableRow className="border-slate-700">
                                                    <TableHead className="text-xs">NC</TableHead>
                                                    <TableHead className="text-xs">Título</TableHead>
                                                    <TableHead className="text-xs">Status</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {data.desempenho_vistorias.nao_conformidades.slice(0, 5).map((nc, idx) => (
                                                    <TableRow key={idx} className="border-slate-700">
                                                        <TableCell className="text-xs font-mono">{nc.numero_nc}</TableCell>
                                                        <TableCell className="text-xs truncate max-w-[100px]">{nc.titulo}</TableCell>
                                                        <TableCell className="text-xs">
                                                            <span className={`px-1 py-0.5 rounded text-[10px] ${
                                                                nc.status === 'encerrada' ? 'bg-green-900 text-green-400' :
                                                                nc.status === 'aberta' ? 'bg-red-900 text-red-400' :
                                                                'bg-yellow-900 text-yellow-400'
                                                            }`}>
                                                                {nc.status}
                                                            </span>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* 3. Performance Geral no Ciclo (30%) */}
                        <Card className="bg-slate-900 border-slate-800">
                            <CardHeader>
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <Activity className="w-4 h-4 text-cyan-400" />
                                    Performance Geral no Ciclo
                                    <span className="ml-auto text-xs bg-cyan-900/50 text-cyan-400 px-2 py-0.5 rounded">
                                        Peso: 30%
                                    </span>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="p-3 bg-slate-800 rounded text-center">
                                    <div className="text-xs text-slate-400">Ciclos Analisados</div>
                                    <div className="text-xl font-bold text-white">
                                        {data.performance_ciclo.ciclos_analisados}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    {Object.entries(data.performance_ciclo.por_grade).map(([grade, info]) => (
                                        <div key={grade} className={`p-3 rounded border ${
                                            info.conforme ? 'bg-green-900/20 border-green-800' : 'bg-red-900/20 border-red-800'
                                        }`}>
                                            <div className="flex justify-between items-center">
                                                <span className="text-xs font-medium">{grade}</span>
                                                {info.conforme ? (
                                                    <CheckCircle className="w-3 h-3 text-green-400" />
                                                ) : (
                                                    <XCircle className="w-3 h-3 text-red-400" />
                                                )}
                                            </div>
                                            <div className="text-lg font-bold mt-1">
                                                {info.percentual_medio.toFixed(2)}%
                                            </div>
                                            <div className="text-[10px] text-slate-400">
                                                Limite: ≤{info.limite}%
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div className="p-3 bg-gradient-to-r from-blue-900/30 to-slate-800 rounded">
                                        <div className="text-xs text-slate-400">Nota VLSFO</div>
                                        <div className={`text-xl font-bold ${data.performance_ciclo.nota_vlsfo > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                            {data.performance_ciclo.nota_vlsfo}/15
                                        </div>
                                    </div>
                                    <div className="p-3 bg-gradient-to-r from-emerald-900/30 to-slate-800 rounded">
                                        <div className="text-xs text-slate-400">Nota MGO</div>
                                        <div className={`text-xl font-bold ${data.performance_ciclo.nota_mgo > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                            {data.performance_ciclo.nota_mgo}/15
                                        </div>
                                    </div>
                                </div>

                                <div className="p-3 bg-cyan-900/30 rounded-lg text-center">
                                    <div className="text-xs text-slate-400">Nota Total Performance</div>
                                    <div className={`text-3xl font-bold ${getNotaColor(data.performance_ciclo.nota, 30)}`}>
                                        {data.performance_ciclo.nota}/30
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Gráficos */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Gráfico de Composição da Nota */}
                        <Card className="bg-slate-900 border-slate-800">
                            <CardHeader>
                                <CardTitle className="text-sm">Composição da Nota Final</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="h-64">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={pieData} layout="vertical">
                                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                            <XAxis type="number" domain={[0, 40]} tick={{ fill: '#94a3b8' }} />
                                            <YAxis type="category" dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} width={100} />
                                            <Tooltip 
                                                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}
                                                formatter={(value, name, props) => [`${value}/${props.payload.max}`, 'Nota']}
                                            />
                                            <Bar dataKey="value" fill="#22d3ee" radius={[0, 4, 4, 0]} />
                                            <Bar dataKey="max" fill="#334155" radius={[0, 4, 4, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Gráfico de Performance por Grade */}
                        <Card className="bg-slate-900 border-slate-800">
                            <CardHeader>
                                <CardTitle className="text-sm">Performance por Grade de Produto</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="h-64">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={gradeData}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                            <XAxis dataKey="grade" tick={{ fill: '#94a3b8' }} />
                                            <YAxis tick={{ fill: '#94a3b8' }} domain={[0, 1]} />
                                            <Tooltip 
                                                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}
                                                formatter={(value) => [`${value.toFixed(3)}%`, 'Percentual']}
                                            />
                                            <Bar 
                                                dataKey="percentual" 
                                                fill="#22d3ee"
                                                radius={[4, 4, 0, 0]}
                                            />
                                            <Bar 
                                                dataKey="limite" 
                                                fill="#ef4444"
                                                radius={[4, 4, 0, 0]}
                                                opacity={0.5}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Resultados por Balsa */}
                    {data.resultados_por_balsa && data.resultados_por_balsa.length > 0 && (
                        <Card className="bg-slate-900 border-slate-800">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Ship className="w-5 h-5 text-cyan-400" />
                                    Resultados por Balsa
                                    <span className="ml-auto text-sm font-normal text-slate-400">
                                        {data.resultados_por_balsa.length} embarcações
                                    </span>
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="overflow-x-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow className="border-slate-700">
                                                <TableHead className="text-xs">Pos.</TableHead>
                                                <TableHead className="text-xs">Embarcação</TableHead>
                                                <TableHead className="text-xs text-center">Operações</TableHead>
                                                <TableHead className="text-xs text-center">Volume (t)</TableHead>
                                                <TableHead className="text-xs text-center">Aval. Média</TableHead>
                                                <TableHead className="text-xs text-center">NCs</TableHead>
                                                <TableHead className="text-xs text-center">
                                                    <span className="text-orange-400">Execução</span>
                                                    <span className="text-slate-500 ml-1">/40</span>
                                                </TableHead>
                                                <TableHead className="text-xs text-center">
                                                    <span className="text-purple-400">Vistorias</span>
                                                    <span className="text-slate-500 ml-1">/30</span>
                                                </TableHead>
                                                <TableHead className="text-xs text-center">
                                                    <span className="text-cyan-400">Performance</span>
                                                    <span className="text-slate-500 ml-1">/30</span>
                                                </TableHead>
                                                <TableHead className="text-xs text-center">
                                                    <span className="text-yellow-400">TOTAL</span>
                                                    <span className="text-slate-500 ml-1">/100</span>
                                                </TableHead>
                                                <TableHead className="text-xs text-center">Classificação</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {data.resultados_por_balsa.map((balsa, idx) => (
                                                <TableRow key={balsa.embarcacao_id} className="border-slate-700 hover:bg-slate-800/50">
                                                    <TableCell className="text-center">
                                                        {idx === 0 ? (
                                                            <Trophy className="w-5 h-5 text-yellow-400 inline" />
                                                        ) : idx === 1 ? (
                                                            <span className="w-5 h-5 inline-flex items-center justify-center rounded-full bg-slate-400 text-slate-900 text-xs font-bold">2</span>
                                                        ) : idx === 2 ? (
                                                            <span className="w-5 h-5 inline-flex items-center justify-center rounded-full bg-amber-600 text-amber-950 text-xs font-bold">3</span>
                                                        ) : (
                                                            <span className="text-slate-500">{idx + 1}</span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center gap-2">
                                                            <Ship className="w-4 h-4 text-slate-400" />
                                                            <span className="font-medium">{balsa.embarcacao_nome}</span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="text-center font-mono">{balsa.total_operacoes}</TableCell>
                                                    <TableCell className="text-center font-mono">{balsa.total_volume.toLocaleString()}</TableCell>
                                                    <TableCell className="text-center">
                                                        {balsa.total_avaliacoes > 0 ? (
                                                            <span className={`font-bold ${
                                                                balsa.avaliacao_media_percent >= 90 ? 'text-green-400' :
                                                                balsa.avaliacao_media_percent >= 70 ? 'text-blue-400' :
                                                                balsa.avaliacao_media_percent >= 50 ? 'text-yellow-400' : 'text-red-400'
                                                            }`}>
                                                                {balsa.avaliacao_media_percent}%
                                                            </span>
                                                        ) : (
                                                            <span className="text-slate-500">-</span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-center">
                                                        <span className={`font-mono ${balsa.total_ncs > 1 ? 'text-red-400' : 'text-green-400'}`}>
                                                            {balsa.total_ncs}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="text-center">
                                                        <span className={`font-bold ${getNotaColor(balsa.nota_execucao, 40)}`}>
                                                            {balsa.nota_execucao}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="text-center">
                                                        <span className={`font-bold ${getNotaColor(balsa.nota_vistorias, 30)}`}>
                                                            {balsa.nota_vistorias}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="text-center">
                                                        <div className="flex flex-col items-center">
                                                            <span className={`font-bold ${getNotaColor(balsa.nota_performance, 30)}`}>
                                                                {balsa.nota_performance}
                                                            </span>
                                                            <span className="text-[10px] text-slate-500">
                                                                V:{balsa.nota_vlsfo} M:{balsa.nota_mgo}
                                                            </span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="text-center">
                                                        <span className={`text-xl font-bold ${
                                                            balsa.nota_total >= 90 ? 'text-green-400' :
                                                            balsa.nota_total >= 70 ? 'text-blue-400' :
                                                            balsa.nota_total >= 50 ? 'text-yellow-400' : 'text-red-400'
                                                        }`}>
                                                            {balsa.nota_total}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="text-center">
                                                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                            balsa.classificacao === 'Excelente' ? 'bg-green-900/50 text-green-400' :
                                                            balsa.classificacao === 'Bom' ? 'bg-blue-900/50 text-blue-400' :
                                                            balsa.classificacao === 'Regular' ? 'bg-yellow-900/50 text-yellow-400' :
                                                            'bg-red-900/50 text-red-400'
                                                        }`}>
                                                            {balsa.classificacao}
                                                        </span>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Histórico Operacional */}
                    {historico && (
                        <div className="space-y-6">
                            <h2 className="text-xl font-bold text-white flex items-center gap-2">
                                <TrendingUp className="w-5 h-5" />
                                Histórico Operacional (Últimos 12 meses)
                            </h2>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Histórico de Operações */}
                                <Card className="bg-slate-900 border-slate-800">
                                    <CardHeader>
                                        <CardTitle className="text-sm">Histórico de Operações</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="h-64">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={historico.historico_operacoes}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                                    <XAxis dataKey="mes_nome" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                                                    <YAxis tick={{ fill: '#94a3b8' }} />
                                                    <Tooltip 
                                                        contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}
                                                    />
                                                    <Legend />
                                                    <Bar dataKey="total_operacoes" name="Operações" fill="#22d3ee" radius={[4, 4, 0, 0]} />
                                                    <Bar dataKey="total_avaliacoes" name="Avaliações" fill="#a855f7" radius={[4, 4, 0, 0]} />
                                                    <Bar dataKey="total_ncs" name="NCs" fill="#ef4444" radius={[4, 4, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Histórico de Notas */}
                                <Card className="bg-slate-900 border-slate-800">
                                    <CardHeader>
                                        <CardTitle className="text-sm">Evolução das Notas</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="h-64">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <LineChart data={historico.historico_notas}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                                    <XAxis dataKey="mes_nome" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                                                    <YAxis domain={[0, 100]} tick={{ fill: '#94a3b8' }} />
                                                    <Tooltip 
                                                        contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}
                                                    />
                                                    <Legend />
                                                    <Line 
                                                        type="monotone" 
                                                        dataKey="nota_total" 
                                                        name="Nota Total"
                                                        stroke="#22d3ee" 
                                                        strokeWidth={3}
                                                        dot={{ fill: '#22d3ee' }}
                                                    />
                                                    <Line 
                                                        type="monotone" 
                                                        dataKey="nota_execucao" 
                                                        name="Execução"
                                                        stroke="#f97316" 
                                                        strokeWidth={2}
                                                        dot={{ fill: '#f97316' }}
                                                    />
                                                    <Line 
                                                        type="monotone" 
                                                        dataKey="nota_vistorias" 
                                                        name="Vistorias"
                                                        stroke="#a855f7" 
                                                        strokeWidth={2}
                                                        dot={{ fill: '#a855f7' }}
                                                    />
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ControleOperacionalPage;
